import type { InstancePlugin, SchedulerPro } from "@bryntum/schedulerpro";
import { CrudQueueTimeoutError } from "../../../lib/CrudQueue";
import type { SwivuProjectModel } from "../../models/SwivuProjectModel";

type FeatureConstructor = new (...args: any[]) => InstancePlugin;
type QueueWork = () => unknown;

enum HoldState {
  Waiting = "waiting",
  Ready = "ready",
  Running = "running",
  RunningAfterTimeout = "runningAfterTimeout",
  Completed = "completed",
  Cancelled = "cancelled",
  Expired = "expired",
  Failed = "failed"
}

interface QueueHold {
  state: HoldState;
  wake: () => void;
  work?: QueueWork;
  completion: Promise<unknown>;
}

/**
 * Reserves one `project.crudQueue.queue()` task for a gesture, per feature instance.
 * User gestures activate only one feature at a time.
 *
 * Normal lifecycle:
 * ```text
 * start -> holdProjectQueue() -> Waiting
 * Waiting --runWithProjectQueue(fn)--> Ready
 * Ready --execution begins--> Running --fn settles--> Completed / Failed -> cleanup
 * ```
 * Execution requires both the queue's turn and handover of fn. Reserving a slot
 * does not stop earlier CRUD; waking the executor does not release the queue.
 *
 * Cancellation before execution (end, abort, or destruction):
 * ```text
 * Waiting / Ready --cancelProjectQueue()--> Cancelled -> release hold
 * queued executor wakes -> skips fn -> finishes its queue step
 * ```
 * Running work cannot be cancelled. Old executor cleanup cannot clear a newer hold.
 *
 * Timeout starts at the queue's turn and includes waiting for handover:
 * ```text
 * Waiting / Ready --timeout--> Expired -> discard fn + release hold
 * Running --timeout--> RunningAfterTimeout --fn settles--> cleanup
 * ```
 * In both cases the queue advances. Already-running work may overlap later CRUD;
 * its hold stays attached until it settles, preventing a new reservation on this feature.
 * Missing or expired reservations enqueue finalization behind current CRUD work.
 * Only projects without a CRUD queue use the immediate-execution fallback.
 */
export default function ProjectQueueHoldMixin<T extends FeatureConstructor>(Target: T) {
  return class ProjectQueueHold extends Target {
    declare readonly client: SchedulerPro & { project: SwivuProjectModel };

    private projectQueueHold?: QueueHold;
    private queueHoldDisposed = false;

    /** Reserve the gesture's FIFO position at its start event. */
    holdProjectQueue(): void {
      const queue = this.client.project.crudQueue;

      if (this.queueHoldDisposed || this.isDestroying || this.projectQueueHold || !queue) return;

      const hold: QueueHold = { state: HoldState.Waiting, wake: () => {}, completion: Promise.resolve() };
      this.projectQueueHold = hold;

      const handover = new Promise<void>((resolve) => {
        hold.wake = resolve;
      });

      const clearHold = () => {
        // A cancelled task may finish after a new gesture has reserved a slot.
        if (this.projectQueueHold === hold) this.projectQueueHold = undefined;
      };

      hold.completion = queue.queue(async () => {
        try {
          await handover;
          // Resolving handover is not permission to run after cancellation/expiry.
          if (hold.state !== HoldState.Ready || this.queueHoldDisposed || this.isDestroying) return;

          hold.state = HoldState.Running;
          const result = await hold.work?.();
          hold.state = HoldState.Completed;
          return result;
        } catch (error) {
          hold.state = HoldState.Failed;
          throw error;
        } finally {
          hold.work = undefined;
          clearHold();
        }
      });

      // Observe the queue's bounded completion separately from raw execution.
      // CrudQueue reports the rejection; this handler only updates ownership.
      void hold.completion.catch((error) => {
        if (hold.state === HoldState.Running && error instanceof CrudQueueTimeoutError) {
          hold.state = HoldState.RunningAfterTimeout;
        } else if (hold.state === HoldState.Waiting || hold.state === HoldState.Ready) {
          hold.state = error instanceof CrudQueueTimeoutError ? HoldState.Expired : HoldState.Failed;
          hold.work = undefined;
          hold.wake();
          clearHold();
        }
      });
    }

    /** Submit finalization once and expose the queue step's bounded completion. */
    runWithProjectQueue(fn: QueueWork): unknown {
      if (this.queueHoldDisposed || this.isDestroying) return;

      this.holdProjectQueue();
      const hold = this.projectQueueHold;
      if (!hold) return fn();

      if (hold.state === HoldState.Waiting) {
        hold.work = fn;
        hold.state = HoldState.Ready;
        hold.wake();
      }

      return hold.completion;
    }

    /** Cancel waiting/handed-over work; never pretend to interrupt running work. */
    cancelProjectQueue() {
      const hold = this.projectQueueHold;
      if (hold?.state === HoldState.Waiting || hold?.state === HoldState.Ready) {
        hold.state = HoldState.Cancelled;
        hold.work = undefined;
        hold.wake();
        this.projectQueueHold = undefined;
      }
    }

    doDestroy() {
      this.queueHoldDisposed = true;
      this.cancelProjectQueue();
      super.doDestroy();
    }
  };
}
